import { apiClient } from './ApiClient'
import { scopedTo } from './activeStartup'
import type {
  Document,
  DocumentBatchUploadItem,
  DocumentBatchUploadResponse,
  DocumentListResponse,
  DocumentUploadResponse,
} from '@/types/api'

/**
 * Query filters for the list endpoint. Each field maps 1:1 to a query
 * string parameter on GET /v1/startups/{id}/documents (see openapi yaml:177).
 */
export interface DocumentListFilters {
  type?: 'pdf' | 'audio' | 'text' | 'url' | 'pptx'
  status?: 'uploading' | 'pending' | 'processing' | 'completed' | 'failed'
  label?: string
  q?: string
}

/** Options carried as multipart form fields alongside the file list. */
export interface DocumentUploadOptions {
  /** Overrides MIME-based type inference on the server. */
  type?: 'pdf' | 'audio' | 'text' | 'url' | 'pptx'
  /** Ignored by the backend for multi-file uploads. */
  name?: string
  /** Comma-joined on the wire. Ignored by backend for multi-file uploads. */
  labels?: string[]
}

/** Union of wire shapes returned by the /documents POST endpoint. */
interface RawUploadResponse {
  success: boolean
  documentId?: string
  storageUrl?: string
  documents?: DocumentBatchUploadItem[]
}

const scopedPath = (startupId: string) =>
  `/v1/startups/${encodeURIComponent(startupId)}/documents`

/**
 * Wraps /v1/startups/{startupId}/documents. Every method is scoped to a
 * single startup — the caller supplies `startupId` explicitly so the service
 * stays stateless. The legacy unscoped methods (uploadDocument/getDocuments/
 * getDocument) are retained as deprecated shims so pages that predate the
 * Phase 1 rewrite still compile; they hit /v1/documents which no longer
 * exists on the backend as of PR #66.
 */
export class DocumentService {
  /** GET /v1/startups/{id}/documents — list with optional filters. */
  async list(
    startupId: string,
    filters?: DocumentListFilters
  ): Promise<DocumentListResponse> {
    const params = filters ? filtersToParams(filters) : undefined
    return apiClient.get<DocumentListResponse>(scopedPath(startupId), params)
  }

  /** POST /v1/startups/{id}/documents — multi-file upload under `files[]`. */
  async upload(
    startupId: string,
    files: File[],
    opts?: DocumentUploadOptions
  ): Promise<DocumentBatchUploadResponse> {
    if (files.length === 0) {
      throw new Error('DocumentService.upload requires at least one file')
    }

    const formData = new FormData()
    for (const file of files) {
      formData.append('files', file)
    }
    if (opts?.type) formData.append('type', opts.type)
    if (opts?.name) formData.append('name', opts.name)
    if (opts?.labels?.length) formData.append('labels', opts.labels.join(','))

    const raw = await apiClient.postMultipart<RawUploadResponse>(
      scopedPath(startupId),
      formData
    )
    return normalizeUpload(raw)
  }

  /**
   * GET /v1/startups/{id}/documents/{docId}.
   *
   * openapi yaml:324 documents a `{ document }` envelope, but the backend
   * currently returns the document bare (documents.ts `res.json(document)`),
   * and returns bare metadata again while status is uploading/pending/
   * processing. Accept either so the call works today and keeps working when
   * the backend is corrected to match the spec.
   */
  async get(startupId: string, docId: string): Promise<Document> {
    const raw = await apiClient.get<Document | { document: Document }>(
      `${scopedPath(startupId)}/${encodeURIComponent(docId)}`
    )
    return raw && typeof raw === 'object' && 'document' in raw ? raw.document : raw
  }

  /**
   * Per-gap upload. Backend `/gaps/{gapId}/documents` has not shipped yet
   * (P0 flag — see API-AUDIT.md). Until it lands, we POST through the
   * general upload path and tag each file with a `gap:<gapId>` label so the
   * backend can still attribute. Once the dedicated endpoint ships, flip
   * this method to the scoped path and keep the same signature.
   */
  async uploadForGap(
    startupId: string,
    gapId: string,
    files: File[]
  ): Promise<DocumentBatchUploadResponse> {
    return this.upload(startupId, files, { labels: [`gap:${gapId}`] })
  }

  // ─── Legacy (unscoped) methods ───────────────────────────────────────
  // Retained so pages under the ESLint exempt list still compile during
  // the wire-up bundle. Scheduled for extraction alongside the other
  // legacy-context methods in a later commit of the bundle.
  // ─────────────────────────────────────────────────────────────────────

  /** @deprecated Use `upload(startupId, [file], opts?)` instead. */
  async uploadDocument(
    file: File,
    type: string,
    name?: string,
    labels?: string[]
  ): Promise<DocumentUploadResponse> {
    const payload: Record<string, string> = { type }
    if (name) payload.name = name
    payload.labels = resolveLegacyLabelsPayload(file, name, labels)
    return apiClient.uploadFile<DocumentUploadResponse>(scopedTo('/documents'), file, payload)
  }

  /** @deprecated Use `list(startupId, filters?)` instead. */
  async getDocuments(): Promise<DocumentListResponse> {
    return apiClient.get<DocumentListResponse>(scopedTo('/documents'))
  }

  /** @deprecated Use `get(startupId, docId)` instead. */
  async getDocument(id: string): Promise<Document> {
    return apiClient.get<Document>(scopedTo(`/documents/${id}`))
  }
}

export const documentService = new DocumentService()

// ─── Local helpers ─────────────────────────────────────────────────────

function filtersToParams(filters: DocumentListFilters): Record<string, string> {
  const rec: Record<string, string> = {}
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') {
      rec[key] = String(value)
    }
  }
  return rec
}

function normalizeUpload(raw: RawUploadResponse): DocumentBatchUploadResponse {
  if (raw.documents?.length) {
    return { success: raw.success, documents: raw.documents }
  }
  if (raw.documentId && raw.storageUrl) {
    return {
      success: raw.success,
      documents: [{ documentId: raw.documentId, storageUrl: raw.storageUrl }],
    }
  }
  return { success: raw.success, documents: [] }
}

function resolveLegacyLabelsPayload(file: File, name?: string, labels?: string[]): string {
  const normalizedLabels = (labels ?? [])
    .map((label) => label.trim())
    .filter((label) => label.length > 0)
  if (normalizedLabels.length > 0) {
    return normalizedLabels.join(',')
  }
  return (name ?? file.name).trim()
}
